import { AfterViewInit, ChangeDetectorRef, Component, ComponentRef, Inject, OnDestroy, OnInit, Optional, ViewChild, ViewContainerRef } from '@angular/core';
import { ActivatedRoute, NavigationStart, Router } from '@angular/router';
import { Subject, distinctUntilChanged, filter, first, forkJoin, map, skip, switchMap, takeUntil } from 'rxjs';
import { inject } from '@angular/core';
import { NavigationConfig } from '../../models/navigation-config';
import { ClerkFacets, ClerkProduct, ClerkSearchService } from '../../services/clerk-search/clerk-search.service';
import { Product } from '../../models/product';
import { ProductListV2ConfigService } from '../../services/product-list-v2-config/product-list-v2-config.service';
import { AvailabilityService } from '../../services/availability/availability.service';
import { PriceService } from '../../services/price/price.service';

@Component({
  selector: 'lib-clerk-search-page',
  templateUrl: './clerk-search-page.component.html',
  styleUrls: ['./clerk-search-page.component.scss'],
  standalone: false
})
export class ClerkSearchPageComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild('layoutHost', { read: ViewContainerRef }) layoutHost: ViewContainerRef;

  products: Product[] = [];
  facets: ClerkFacets = {};
  facetEntries: { key: string; items: any[] }[] = [];
  hasFacets = false;
  hasMore = false;
  selectedFilters: { [key: string]: number | string | null } = {};
  openFacets: { [key: string]: boolean } = {};
  totalCount = 0;
  isLoading = false;
  searchValue = '';

  readonly pageLimit = 32;
  private offset = 0;
  private readonly unsubscribe = new Subject<void>();
  private layoutComponentRef: ComponentRef<any>;
  private clerkProductsCache: ClerkProduct[] = [];

  private readonly clerkSearchService = inject(ClerkSearchService);
  private readonly productListV2ConfigService = inject(ProductListV2ConfigService);
  private readonly availabilityService = inject(AvailabilityService);
  private readonly priceService = inject(PriceService);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);

  readonly apiKey: string;

  constructor(
    @Optional() @Inject('environment') environment: NavigationConfig
  ) {
    this.apiKey = environment?.clerkApiKey || '';
  }

  /**
   * Maps Clerk products to gung Product objects using the data already in the Clerk response.
   * When a Clerk→gung ID mapping endpoint is available, override this method to:
   *   1. Map clerkProducts IDs via the mapping endpoint
   *   2. Fetch full Product objects from the gung backend with ProductService.getProductsByIds()
   */
  protected mapClerkProductsToGungProducts(clerkProducts: ClerkProduct[]): Product[] {
    return clerkProducts.map(cp => ({
      id: String(cp.id),
      name: cp.name,
      productType: cp.productType || cp.type || (cp.url?.includes('/concept/') ? 'concept' : 'product'),
      extra: {
        images: cp.image ? [{ s3Uri: cp.image, s3ETag: '' }] : []
      }
    } as unknown as Product));
  }

  ngOnInit(): void {
    const snapshot = this.route.snapshot.queryParamMap;
    this.searchValue = snapshot.get('query') || '';
    snapshot.keys.filter(k => k !== 'query').forEach(key => {
      const val = snapshot.get(key);
      if (val != null) {
        this.selectedFilters[key] = val !== '' && !isNaN(Number(val)) ? Number(val) : val;
      }
    });
    this.fetchResults();

    this.route.queryParamMap.pipe(
      takeUntil(this.unsubscribe),
      map(params => params.get('query') || ''),
      distinctUntilChanged(),
      skip(1)
    ).subscribe(query => {
      this.searchValue = query;
      this.selectedFilters = {};
      this.facets = {};
      this.facetEntries = [];
      this.hasFacets = false;
      this.openFacets = {};
      this.offset = 0;
      this.products = [];
      this.hasMore = false;
      this.router.navigate([], { relativeTo: this.route, queryParams: { query }, replaceUrl: true });
      this.fetchResults();
    });

    this.router.events.pipe(
      takeUntil(this.unsubscribe),
      filter(e => e instanceof NavigationStart)
    ).subscribe((e: NavigationStart) => {
      if (!this.apiKey || !this.clerkProductsCache.length) {
        return;
      }
      const navPath = e.url.split('?')[0];
      const clicked = this.clerkProductsCache.find(p => {
        if (!p.url) {
          return false;
        }
        try {
          return new URL(p.url).pathname === navPath;
        } catch {
          return p.url.split('?')[0] === navPath;
        }
      });
      if (clicked) {
        this.clerkSearchService.logClick(this.apiKey, clicked.id, 'Search - Search page', 'search/search').subscribe();
      }
    });
  }

  ngAfterViewInit(): void {
    if (this.layoutHost) {
      const ListItemComponent = this.productListV2ConfigService.getLayouts()[0].getListItemComponent();
      this.layoutComponentRef = this.layoutHost.createComponent(ListItemComponent);
      this.layoutComponentRef.setInput('data', this.products);
      this.cdr.detectChanges();
    }
  }

  ngOnDestroy(): void {
    this.unsubscribe.next();
    this.unsubscribe.complete();
  }

  private buildFilter(): string | undefined {
    const parts = Object.entries(this.selectedFilters)
      .filter(([, v]) => v != null)
      .map(([key, value]) => `${key} = ${value}`);
    return parts.length > 0 ? parts.join(' AND ') : undefined;
  }

  private fetchResults(append = false): void {
    if (!this.searchValue || !this.apiKey) {
      return;
    }
    this.isLoading = true;

    this.clerkSearchService
      .search(this.searchValue, this.apiKey, this.pageLimit, this.offset, ['categories'], this.buildFilter())
      .pipe(
        takeUntil(this.unsubscribe),
        switchMap(response => {
          const mapped = this.mapClerkProductsToGungProducts(response.product_data);
          const ids = mapped.map(p => p.id);
          return forkJoin({
            avs: this.availabilityService.getAvailabilities(ids).pipe(first()),
            prices: this.priceService.getCurrentCustomerPrices(ids).pipe(first())
          }).pipe(
            map(({ avs, prices }) => ({ response, mapped, avs, prices }))
          );
        })
      )
      .subscribe(({ response, mapped, avs, prices }) => {
        if (!append && response.facets) {
          this.facets = response.facets;
          this.facetEntries = Object.entries(response.facets).map(([key, items]) => ({ key, items }));
          this.hasFacets = this.facetEntries.length > 0;
          Object.keys(response.facets).forEach(k => {
            if (this.openFacets[k] === undefined) {
              this.openFacets[k] = true;
            }
          });
        }
        this.totalCount = response.hits;
        this.clerkProductsCache = append
          ? [...this.clerkProductsCache, ...response.product_data]
          : response.product_data;
        const products = mapped
          .filter(p => p.productType === 'concept' || prices.some(price => price.productId === p.id))
          .map(p => {
            const av = avs.find(a => a.productId === p.id);
            if (av) {
              p.extra.availabilities = { [av.productId]: av };
            }
            return p;
          });
        this.products = append ? [...this.products, ...products] : products;
        this.hasMore = this.products.length < this.totalCount;
        if (this.layoutComponentRef) {
          this.layoutComponentRef.setInput('data', this.products);
        }
        this.isLoading = false;
      });
  }

  selectFacet(key: string, value: number | string): void {
    this.selectedFilters = {
      ...this.selectedFilters,
      [key]: this.selectedFilters[key] === value ? null : value
    };
    this.offset = 0;
    this.products = [];

    const queryParams: { [key: string]: any } = { query: this.searchValue };
    Object.entries(this.selectedFilters).forEach(([k, v]) => {
      if (v != null) queryParams[k] = v;
    });
    this.router.navigate([], { relativeTo: this.route, queryParams, replaceUrl: true });

    this.fetchResults();
  }

  loadMore(): void {
    this.offset += this.pageLimit;
    this.fetchResults(true);
  }

}
